import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { MousePointerClick, ArrowRight } from 'lucide-react';
interface SlideCTALiftProps {
  onContinue: () => void;
}
export function SlideCTALift({
  onContinue
}: SlideCTALiftProps) {
  const [engagedHCPs, setEngagedHCPs] = useState(20000);
  const [baseCTA, setBaseCTA] = useState(12);
  const [ctaLift, setCtaLift] = useState(2);
  const [campaigns, setCampaigns] = useState(16);
  const {
    flatActions,
    compActions
  } = useMemo(() => {
    let flat = 0;
    let comp = 0;
    const engagedPerCampaign = engagedHCPs / campaigns;
    for (let i = 0; i < campaigns; i++) {
      const flatRate = baseCTA / 100;
      const compRate = baseCTA / 100 * Math.pow(1 + ctaLift / 100, i);
      flat += engagedPerCampaign * flatRate;
      comp += engagedPerCampaign * compRate;
    }
    return {
      flatActions: flat,
      compActions: comp
    };
  }, [engagedHCPs, baseCTA, ctaLift, campaigns]);
  const extraActions = compActions - flatActions;
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-159">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-160">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-161">
          From Engagement to Action
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-162">
          Engagement is good.{' '}
          <span className="text-teal-400" data-id="element-163">Action is better.</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-164">
          When we learn what HCPs respond to, we can improve not only
          engagement, but also the next click, scan, meeting request or content
          view.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8" data-id="element-165">
        {/* LEFT — Inputs & Funnel */}
        <div className="lg:col-span-5 space-y-6" data-id="element-166">
          <div className="bg-navy-900/70 p-5 rounded-2xl border border-navy-600 space-y-5" data-id="element-167">
            <div className="space-y-1.5" data-id="element-168">
              <div className="flex justify-between text-sm" data-id="element-169">
                <label className="text-white/70" data-id="element-170">Engaged HCPs per year</label>
                <span className="text-white font-medium" data-id="element-171">
                  {engagedHCPs.toLocaleString()}
                </span>
              </div>
              <input type="range" min="5000" max="50000" step="1000" value={engagedHCPs} onChange={e => setEngagedHCPs(Number(e.target.value))} className="w-full accent-teal-400" data-id="element-172" />
            </div>

            <div className="space-y-1.5" data-id="element-173">
              <div className="flex justify-between text-sm" data-id="element-174">
                <label className="text-white/70" data-id="element-175">Starting CTA conversion</label>
                <span className="text-white font-medium" data-id="element-176">{baseCTA}%</span>
              </div>
              <input type="range" min="2" max="25" step="1" value={baseCTA} onChange={e => setBaseCTA(Number(e.target.value))} className="w-full accent-teal-400" data-id="element-177" />
            </div>

            <div className="space-y-1.5" data-id="element-178">
              <div className="flex justify-between text-sm" data-id="element-179">
                <label className="text-white/70" data-id="element-180">CTA lift per campaign</label>
                <span className="text-teal-400 font-medium" data-id="element-181">+{ctaLift}%</span>
              </div>
              <input type="range" min="1" max="10" step="1" value={ctaLift} onChange={e => setCtaLift(Number(e.target.value))} className="w-full accent-teal-400" data-id="element-182" />
            </div>

            <div className="space-y-1.5" data-id="element-183">
              <div className="flex justify-between text-sm" data-id="element-184">
                <label className="text-white/70" data-id="element-185">Campaigns per year</label>
                <span className="text-white font-medium" data-id="element-186">{campaigns}</span>
              </div>
              <input type="range" min="4" max="32" step="1" value={campaigns} onChange={e => setCampaigns(Number(e.target.value))} className="w-full accent-teal-400" data-id="element-187" />
            </div>
          </div>

          {/* Funnel Visual */}
          <div className="bg-navy-800/40 border border-navy-700 rounded-xl p-6 flex flex-col items-center justify-center gap-2" data-id="element-188">
            <div className="w-full bg-navy-700/50 rounded py-2 text-center text-white/50 text-xs tracking-widest uppercase" data-id="element-189">
              Reach
            </div>
            <ArrowRight className="w-4 h-4 text-white/20 rotate-90" data-id="element-190" />
            <div className="w-4/5 bg-navy-600/50 rounded py-2 text-center text-white/70 text-xs tracking-widest uppercase" data-id="element-191">
              Engagement
            </div>
            <ArrowRight className="w-4 h-4 text-teal-400/50 rotate-90" data-id="element-192" />
            <div className="w-3/5 bg-teal-400/20 border border-teal-400/30 rounded py-2 text-center text-teal-400 text-xs tracking-widest uppercase font-medium shadow-[0_0_15px_rgba(45,212,191,0.2)]" data-id="element-193">
              CTA Action
            </div>
            <ArrowRight className="w-4 h-4 text-teal-400/50 rotate-90" data-id="element-194" />
            <div className="w-2/5 bg-teal-400/40 border border-teal-400/50 rounded py-2 text-center text-white text-xs tracking-widest uppercase font-bold" data-id="element-195">
              Follow-up
            </div>
          </div>
        </div>

        {/* RIGHT — Outputs */}
        <div className="lg:col-span-7 flex flex-col space-y-4 justify-center" data-id="element-196">
          <div className="bg-navy-800/60 border border-navy-600 rounded-2xl p-6" data-id="element-197">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-2" data-id="element-198">
              Flat CTA model
            </p>
            <div className="text-3xl font-serif text-white/70" data-id="element-199">
              {Math.round(flatActions).toLocaleString()}{' '}
              <span className="text-lg font-sans" data-id="element-200">CTA actions/year</span>
            </div>
            <p className="text-white/40 text-sm mt-2" data-id="element-201">
              Same conversion every campaign
            </p>
          </div>

          <div className="bg-teal-400/5 border border-teal-400/30 rounded-2xl p-6" data-id="element-202">
            <p className="text-teal-300 text-[10px] uppercase tracking-widest font-medium mb-2" data-id="element-203">
              Learning CTA model
            </p>
            <motion.div key={compActions} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-3xl font-serif text-teal-400" data-id="element-204">
              Approx. {Math.round(compActions).toLocaleString()}–
              {Math.round(compActions * 1.1).toLocaleString()}{' '}
              <span className="text-lg font-sans" data-id="element-205">CTA actions/year</span>
            </motion.div>
            <p className="text-teal-300/60 text-sm mt-2" data-id="element-206">
              CTA improves as the campaign learns
            </p>
          </div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="bg-teal-400/10 border border-teal-400/40 rounded-2xl p-6 flex items-center gap-5" data-id="element-207">
            <MousePointerClick className="w-8 h-8 text-teal-400 flex-shrink-0" data-id="element-208" />
            <div data-id="element-209">
              <div className="text-white/50 text-[10px] uppercase tracking-widest font-medium" data-id="element-210">
                Extra actions created
              </div>
              <div className="flex items-baseline gap-2 mt-1" data-id="element-211">
                <motion.span key={extraActions} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-4xl font-serif text-teal-400 tracking-tight" data-id="element-212">
                  +{Math.round(extraActions).toLocaleString()} to +
                  {Math.round(extraActions * 1.5).toLocaleString()}
                </motion.span>
              </div>
              <p className="text-white/70 text-sm mt-2" data-id="element-213">
                additional HCP actions/year without increasing reach or media
                budget.
              </p>
            </div>
          </motion.div>

          <p className="text-teal-300/80 text-sm border-l-2 border-teal-400/40 pl-3 py-1 mt-4" data-id="element-214">
            This is where Snakker moves from content production to commercial
            leverage.
          </p>
        </div>
      </div>

      <div className="flex justify-end mt-4" data-id="element-215">
        <Button onClick={onContinue} data-id="element-216">See the operating model →</Button>
      </div>
    </div>;
}